/**
 * useTenantMigration
 * ─────────────────────────────────────────────────────────────────
 * One-time migration hook that runs when a user first logs in as a
 * "cloud" tenant (has tenantId) but still has data in local IndexedDB.
 *
 * It copies all known collections from IndexedDB → DynamoDB then marks
 * migration complete in localStorage so it never runs again.
 */

import { useEffect, useState } from 'react';
import * as localDB from '../utils/indexedDBService';
import { TenantDataService } from '../services/tenant-data-service';

// Collections to migrate (matches IndexedDB store names used across the app)
const COLLECTIONS = [
  'inventory',
  'inventoryCategories',
  'clients',
  'vendors',
  'purchases',
  'invoices',
  'expenses',
  'cashTransactions',
  'bankTransactions',
];

interface MigrationStatus {
  done: boolean;
  running: boolean;
  migrated: number;
  error: string | null;
}

function getMigrationKey(userId: string) {
  return `tenantMigration_v2_${userId}`;
}

export function useTenantMigration(
  userId: string | undefined,
  db: TenantDataService | null
): MigrationStatus {
  const [status, setStatus] = useState<MigrationStatus>({
    done: false,
    running: false,
    migrated: 0,
    error: null,
  });

  useEffect(() => {
    if (!userId || !db || !db.isCloud) return;

    // Already migrated?
    const key = getMigrationKey(userId);
    if (localStorage.getItem(key) === 'done') {
      setStatus(s => ({ ...s, done: true }));
      return;
    }

    let cancelled = false;

    const migrate = async () => {
      setStatus({ done: false, running: true, migrated: 0, error: null });

      try {
        await localDB.initDB();
        let totalMigrated = 0;

        for (const collection of COLLECTIONS) {
          if (cancelled) break;

          let localItems: any[] = [];
          try {
            localItems = await localDB.getAllItems<any>(collection);
          } catch {
            // Store may not exist yet — skip
            continue;
          }

          if (!localItems || localItems.length === 0) continue;

          console.log(`[Migration] ${collection}: uploading ${localItems.length} items...`);
          for (const item of localItems) {
            if (!item.id) continue; // skip items without an id
            try {
              await db.put(collection, item);
              totalMigrated++;
            } catch (err) {
              console.warn(`[Migration] Failed to upload item ${item.id} in ${collection}:`, err);
            }
          }
        }

        if (!cancelled) {
          localStorage.setItem(key, 'done');
          console.log(`[Migration] Complete — ${totalMigrated} items migrated to cloud.`);
          setStatus({ done: true, running: false, migrated: totalMigrated, error: null });
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('[Migration] Error during tenant migration:', err);
          setStatus({ done: false, running: false, migrated: 0, error: err.message });
        }
      }
    };

    migrate();

    return () => { cancelled = true; };
  }, [userId, db?.isCloud]); // eslint-disable-line react-hooks/exhaustive-deps

  return status;
}
